import { Select, SelectItem, SelectOptionGroup, Spinner } from "@swivl-mono/ui";
import { useMemo } from "react";

import LocationsModel from "../../../Models/Locations/Locations.Model";

interface Props {
  type: LocationsAndGroupsDropdownType;
  groupId?: number;
  locationId?: number;
  name?: string;
  deselectLabel?: string;
  /** Hide the Select's clear (✕) button. Defaults to false (clear shown). */
  hideClear?: boolean;
  /**
   * LOCATIONS mode only: restrict the options to a single group's locations
   * (ENG-2752). Used where a group lock is already active, so the picker can't
   * offer a location the lock would exclude. Undefined lists every group.
   */
  filterToGroupId?: number;
  onSelect(groupId?: number, locationId?: number): void;
}
export enum LocationsAndGroupsDropdownType {
  LOCATIONS = "Locations",
  GROUPS = "Groups",
  LOCATIONS_AND_GROUPS = "Locations and Groups"
}
export default function LocationsAndGroupsDropdown(props: Props) {
  const { type, groupId, locationId, onSelect, deselectLabel, filterToGroupId } = props;
  let { groups } = LocationsModel.useState((s) => s);

  const options = useMemo((): SelectItem[] | null => {
    if (!groups) {
      return null;
    }

    const prepend: SelectItem[] = deselectLabel ? [{ value: "", label: deselectLabel }] : [];

    if (type === LocationsAndGroupsDropdownType.LOCATIONS) {
      const scoped = filterToGroupId != null ? groups.filter((group) => group.id === filterToGroupId) : groups;
      const grouped: SelectItem[] = scoped.map(
        (group): SelectOptionGroup => ({
          type: "group",
          label: group.name,
          options: group.locations.map((location) => ({ value: String(location.id), label: location.name }))
        })
      );
      return [...prepend, ...grouped];
    }

    if (type === LocationsAndGroupsDropdownType.GROUPS) {
      return [...prepend, ...groups.map((group) => ({ value: String(group.id), label: group.name }))];
    }

    if (type === LocationsAndGroupsDropdownType.LOCATIONS_AND_GROUPS) {
      const flat: SelectItem[] = [];
      groups.forEach((group) => {
        flat.push({ value: `g:${group.id}`, label: group.name });
        group.locations.forEach((location) => {
          flat.push({ value: `l:${location.id}`, label: location.name });
        });
      });
      return [...prepend, ...flat];
    }

    return prepend;
  }, [groups, type, deselectLabel, filterToGroupId]);

  if (!groups) {
    return <Spinner />;
  }

  let selectedValue: string | undefined;
  if (type === LocationsAndGroupsDropdownType.GROUPS) {
    selectedValue = groupId ? String(groupId) : undefined;
  } else if (type === LocationsAndGroupsDropdownType.LOCATIONS) {
    selectedValue = locationId ? String(locationId) : undefined;
  } else if (type === LocationsAndGroupsDropdownType.LOCATIONS_AND_GROUPS) {
    if (groupId) selectedValue = `g:${groupId}`;
    else if (locationId) selectedValue = `l:${locationId}`;
  }

  const handleChange = (selected: string[]) => {
    const val = selected[0];
    if (!val) {
      onSelect(undefined, undefined);
      return;
    }
    if (type === LocationsAndGroupsDropdownType.GROUPS) {
      onSelect(parseInt(val), undefined);
    } else if (type === LocationsAndGroupsDropdownType.LOCATIONS) {
      onSelect(undefined, parseInt(val));
    } else if (type === LocationsAndGroupsDropdownType.LOCATIONS_AND_GROUPS) {
      if (val.startsWith("g:")) {
        onSelect(parseInt(val.slice(2)), undefined);
      } else if (val.startsWith("l:")) {
        onSelect(undefined, parseInt(val.slice(2)));
      }
    }
  };

  return (
    <Select
      options={options ?? []}
      selectedValues={selectedValue ? [selectedValue] : undefined}
      name={props.name}
      onChange={handleChange}
      hideClear={props.hideClear}
      hasSearchInDropdown
    />
  );
}
